import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { Card } from "../../components/ui/card";
import {
  TrendingUp,
  Users,
  DollarSign,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  Building2,
  Star,
  CheckCircle,
  Clock,
  Calendar,
  ArrowRight,
  Download,
  Filter
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { getAdminBusinessAnalytics } from "../../redux/Actions/AdminAction";

// Mock data for charts
const revenueData = [
    { month: "Jan", revenue: 45000, growth: 12 },
    { month: "Feb", revenue: 52000, growth: 15 },
    { month: "Mar", revenue: 49000, growth: -5 },
    { month: "Apr", revenue: 58000, growth: 18 },
    { month: "May", revenue: 63000, growth: 8 },
    { month: "Jun", revenue: 72000, growth: 14 },
    { month: "Jul", revenue: 68000, growth: -5 }
  ];
  
  const businessGrowthData = [
    { month: "Jan", newBusinesses: 120, activeBusinesses: 1100 },
    { month: "Feb", newBusinesses: 140, activeBusinesses: 1200 },
    { month: "Mar", newBusinesses: 135, activeBusinesses: 1250 },
    { month: "Apr", newBusinesses: 160, activeBusinesses: 1350 },
    { month: "May", newBusinesses: 180, activeBusinesses: 1450 },
    { month: "Jun", newBusinesses: 190, activeBusinesses: 1550 },
    { month: "Jul", newBusinesses: 210, activeBusinesses: 1650 }
  ];
  
  const categoryData = [
    { name: "Plumbing", value: 30 },
    { name: "Electrical", value: 25 },
    { name: "Legal", value: 20 },
    { name: "Cleaning", value: 15 },
    { name: "Others", value: 10 }
  ];
  
  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

const BusinessAnalytics = () => {
  const [timeRange, setTimeRange] = useState("week");
  const [isLoadingData, setIsLoadingData] = useState(false);
  
  // Redux state and dispatch
  const dispatch = useDispatch();
  const { businessAnalytics } = useSelector((state: any) => state.admin);

  // Fetch business analytics data on component mount and when time range changes
  useEffect(() => {
    setIsLoadingData(true);
    dispatch(getAdminBusinessAnalytics({ isShowLoader: false, period: timeRange }) as any)
      .finally(() => setIsLoadingData(false));
  }, [dispatch, timeRange]);

  // Use real API data or fallback to mock data
  const revenueData = businessAnalytics?.revenues || [];
  
  const businessGrowthData = businessAnalytics?.contracts || [];
  
  const categoryData = businessAnalytics?.categories || [];

  // Show loading state if no data yet or loading
  if (isLoadingData || !businessAnalytics) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
        <AdminHeader />
        <main className="flex-1 lg:pl-64 pt-16">
          <div className="p-6">
            <div className="max-w-7xl mx-auto space-y-6">
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">
                    {isLoadingData ? `Loading ${timeRange} business analytics...` : "Loading business analytics..."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
                    {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Business Analytics</h1>
            <p className="text-gray-600 mt-1">Monitor and analyze business performance metrics</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filters
            </Button>
            <Button variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="flex gap-2">
          {["day", "week", "month", "quarter", "year"].map((range) => (
            <Button
              key={range}
              variant={timeRange === range ? "default" : "outline"}
              onClick={() => setTimeRange(range)}
              className="px-4"
            >
              {range === "day" ? "24 Hours" : 
               range === "week" ? "7 Days" : 
               range === "month" ? "30 Days" : 
               range === "quarter" ? "Quarter" : 
               range === "year" ? "Year" : range}
            </Button>
          ))}
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-semibold mt-1">
                  ${revenueData.length > 0 ? revenueData.reduce((sum, item) => sum + item.revenue, 0).toLocaleString() : "0"}
                </p>
                <div className={`flex items-center mt-2 ${
                  revenueData.length > 0 && revenueData[revenueData.length - 1]?.isPositive ? "text-green-600" : "text-red-600"
                }`}>
                  {revenueData.length > 0 && revenueData[revenueData.length - 1]?.isPositive ? (
                    <ArrowUpRight className="h-4 w-4" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4" />
                  )}
                  <span className="text-sm">
                    {revenueData.length > 0 && revenueData[revenueData.length - 1]?.isPositive ? "+" : ""}{revenueData.length > 0 ? revenueData[revenueData.length - 1]?.percentageChange || "0" : "0"}%
                  </span>
                </div>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500">Active Businesses</p>
                <p className="text-2xl font-semibold mt-1">
                  {businessGrowthData.length > 0 ? businessGrowthData[businessGrowthData.length - 1]?.activeBusinesses?.toLocaleString() || "0" : "0"}
                </p>
                <div className={`flex items-center mt-2 ${
                  businessGrowthData.length > 0 && businessGrowthData[businessGrowthData.length - 1]?.isPositive ? "text-green-600" : "text-red-600"
                }`}>
                  {businessGrowthData.length > 0 && businessGrowthData[businessGrowthData.length - 1]?.isPositive ? (
                    <ArrowUpRight className="h-4 w-4" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4" />
                  )}
                  <span className="text-sm">
                    {businessGrowthData.length > 0 && businessGrowthData[businessGrowthData.length - 1]?.isPositive ? "+" : ""}{businessGrowthData.length > 0 ? businessGrowthData[businessGrowthData.length - 1]?.percentageChange || "0" : "0"}%
                  </span>
                </div>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <Building2 className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500">Service Categories</p>
                <p className="text-2xl font-semibold mt-1">
                  {categoryData.length || "0"}
                </p>
                <div className="flex items-center mt-2 text-gray-500">
                  <span className="text-sm">
                    {categoryData.length} total categories
                  </span>
                </div>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <Globe className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500">Average Rating</p>
                <p className="text-2xl font-semibold mt-1">
                  {businessAnalytics?.reviews?.averageRating?.toFixed(1) || "0.0"}
                </p>
                <div className="flex items-center mt-2 text-gray-500">
                  <span className="text-sm">
                    {businessAnalytics?.reviews?.totalReviews || "0"} reviews
                  </span>
                </div>
              </div>
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Chart */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-6">Revenue Growth</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`$${value}`, "Revenue"]} />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#2563eb"
                    fill="#93c5fd"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Business Growth Chart */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-6">Business Growth</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={businessGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="newBusinesses"
                    stroke="#2563eb"
                    strokeWidth={2}
                    name="New Businesses"
                  />
                  <Line
                    type="monotone"
                    dataKey="activeBusinesses"
                    stroke="#16a34a"
                    strokeWidth={2}
                    name="Active Businesses"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Category Distribution */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-6">Category Distribution</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value}`, "Count"]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Monthly Growth Rate */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-6">Monthly Growth Rate</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value}%`, "Growth"]} />
                  <Bar dataKey="percentageChange" fill="#2563eb">
                    {revenueData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.isPositive ? "#16a34a" : "#dc2626"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Additional Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Top Performing Categories</h3>
            <div className="space-y-4">
              {categoryData.slice(0, 3).map((category, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={"w-2 h-2 rounded-full"} style={{ backgroundColor: COLORS[index] }} />
                    <span>{category.name}</span>
                  </div>
                  <span className="font-medium">{category.value}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Growth Indicators</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Revenue Growth</span>
                <Badge variant={undefined} className={`${revenueData.length > 0 && revenueData[revenueData.length - 1]?.isPositive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                  {revenueData.length > 0 && revenueData[revenueData.length - 1]?.isPositive ? "+" : ""}{revenueData.length > 0 ? revenueData[revenueData.length - 1]?.percentageChange || "0" : "0"}%
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Business Growth</span>
                <Badge variant={undefined} className={`${businessGrowthData.length > 0 && businessGrowthData[businessGrowthData.length - 1]?.isPositive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                  {businessGrowthData.length > 0 && businessGrowthData[businessGrowthData.length - 1]?.isPositive ? "+" : ""}{businessGrowthData.length > 0 ? businessGrowthData[businessGrowthData.length - 1]?.percentageChange || "0" : "0"}%
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Categories</span>
                <Badge variant={undefined} className="bg-purple-100 text-purple-700">{categoryData.length}</Badge>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Review Summary</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span>{businessAnalytics?.reviews?.totalReviews || "0"} Total Reviews</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span>{businessAnalytics?.reviews?.averageRating?.toFixed(1) || "0.0"} Average Rating</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span>{businessAnalytics?.reviews?.positiveReviews || "0"} Positive Reviews</span>
              </div>
            </div>
          </Card>
        </div>
          </div>
        </div>
      </main>

      <br></br>
      <br></br>
      <br></br>
      <br></br>

      {/* <SharedFooter3 /> */}
    </div>
  );
};

export default BusinessAnalytics;